/**
 * Shared UI widgets — small pure builders reused across screens. Each returns a
 * detached DOM node; screens compose them. Keeps the GBC vocabulary consistent.
 */

import { el, div, span } from "./dom";
import {
  statusGlyph,
  statusLabel,
  statusClass,
  cardArtGlyph,
} from "./glyphs";
import { getCardDef } from "../engine";
import type {
  CardDef,
  RunState,
  StatusKind,
  StatusMap,
} from "../engine/types";

/** Top status bar: HP, gold, act/floor, programs, seed. */
export function topbar(run: RunState, onPrograms?: () => void): HTMLElement {
  const programs = div("programs");
  for (const pid of run.programs.slice(0, 10)) {
    programs.appendChild(
      el("span", {
        class: "pip",
        title: pid,
        text: programGlyph(pid),
      }),
    );
  }
  const bar = div("topbar", [
    div("stat hp", [span("", "HP"), el("b", { text: `${run.hp}/${run.maxHp}` })]),
    div("stat gold", [span("", "₵"), el("b", { text: String(run.gold) })]),
    div("stat", [span("", "ACT"), el("b", { text: String(run.map.act) })]),
    run.heat > 0
      ? div("stat", [span("", "HEAT"), el("b", { text: String(run.heat) })])
      : null,
    div("spacer"),
    programs,
    el("span", { class: "seed", text: `SEED ${run.seed}` }),
  ].filter(Boolean) as Node[]);
  if (onPrograms) {
    programs.style.cursor = "pointer";
    programs.addEventListener("click", onPrograms);
  }
  return bar;
}

function programGlyph(_pid: string): string {
  return "◆";
}

/** A horizontal HP bar with numeric label. */
export function hpBar(hp: number, maxHp: number, width = 200): HTMLElement {
  const pct = maxHp > 0 ? Math.max(0, Math.min(100, (hp / maxHp) * 100)) : 0;
  const low = hp / Math.max(1, maxHp) <= 0.3;
  const fill = div(`bar__fill hp${low ? " low" : ""}`);
  fill.style.width = `${pct}%`;
  const bar = div("bar", [fill, div("bar__label", [`${hp} / ${maxHp}`])]);
  bar.style.width = `${width}px`;
  return bar;
}

/**
 * The Trace meter — segmented bar that escalates color as it climbs, plus the
 * hard-countdown readout. Visualizes the signature tension mechanic.
 */
export function traceMeter(
  value: number,
  max: number,
  hardCountdown: number,
  tiers: readonly number[] = [6, 11, 16],
): HTMLElement {
  const segs = div("trace__segs");
  for (let i = 1; i <= max; i++) {
    const on = i <= value;
    let cls = "trace__seg";
    if (tiers.includes(i)) cls += " tier";
    if (on) {
      cls += " on";
      // Color by how close to max.
      const ratio = i / max;
      if (ratio > 0.82) cls += " hot";
      else if (ratio > 0.55) cls += " warn";
    }
    segs.appendChild(div(cls));
  }
  const headRight =
    hardCountdown > 0
      ? el("span", {
          class: "trace__countdown",
          text: `⚠ COUNTDOWN ${hardCountdown}`,
        })
      : el("span", { class: "val", text: `${value} / ${max}` });
  return div("trace", [
    div("trace__head", [span("", "TRACE"), headRight]),
    segs,
  ]);
}

/** Render an actor's status pips (block + statuses). */
export function statusPips(
  statuses: StatusMap | undefined,
  block?: number,
): HTMLElement {
  const pips = div("pips");
  if (block && block > 0) {
    pips.appendChild(
      el("span", {
        class: "pip block",
        title: "Block",
        html: `▣<b>${block}</b>`,
      }),
    );
  }
  if (statuses) {
    for (const key of Object.keys(statuses) as StatusKind[]) {
      if (key === "block") continue; // shown via `block`
      const n = statuses[key] ?? 0;
      if (n <= 0) continue;
      pips.appendChild(
        el("span", {
          class: `pip ${statusClass(key)}`,
          title: statusLabel(key),
          html: `${statusGlyph(key)}<b>${n}</b>`,
        }),
      );
    }
  }
  return pips;
}

/**
 * A full card face. `opts.playable` controls hover affordance; `opts.affordable`
 * tints the cost; `opts.onClick` makes it interactive.
 */
export function cardFace(
  defId: string,
  opts: {
    playable?: boolean;
    affordable?: boolean;
    selected?: boolean;
    focused?: boolean;
    onClick?: () => void;
    title?: string;
  } = {},
): HTMLElement {
  const def: CardDef = getCardDef(defId);
  const unplayable = def.unplayable || opts.playable === false;
  let cls = "card";
  if (def.upgraded) cls += " upgraded";
  if (unplayable) cls += " unplayable";
  else cls += " playable";
  if (opts.selected) cls += " selected";
  if (opts.focused) cls += " is-focus";

  const costCls = `card__cost${opts.affordable === false ? " unaffordable" : ""}`;
  const card = div(cls, [
    div(costCls, [String(def.cost)]),
    div("card__name", [def.name]),
    div(`card__type ${def.type}`, [def.type]),
    div("card__art", [cardArtGlyph(def.type)]),
    div("card__desc", [def.description]),
  ]);
  if (opts.title) card.title = opts.title;
  if (opts.onClick && !unplayable) card.addEventListener("click", opts.onClick);
  else if (opts.onClick) {
    // Unplayable cards can still be clicked for info in some contexts.
    card.addEventListener("click", opts.onClick);
  }
  return card;
}

/** Compact one-line card row (deck lists, reward lists). */
export function cardRow(
  defId: string,
  opts: { focused?: boolean; onClick?: () => void } = {},
): HTMLElement {
  const def = getCardDef(defId);
  const row = div(`cardrow${opts.focused ? " is-focus" : ""}`, [
    div("cr-cost", [String(def.cost)]),
    el("span", { class: "cr-name", text: def.name }),
    el("span", { class: "cr-desc", text: def.description }),
  ]);
  if (opts.onClick) row.addEventListener("click", opts.onClick);
  return row;
}
